import { API_URL } from '../config';
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { templatesApi, Template, Botao } from '../api/api';

function fmtDate(d: string) {
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const PROPOSAL_VARIABLES = [
  { label: 'Nome', token: '{nome}' },
  { label: 'Proposta', token: '{proposta}' },
  { label: 'Valor', token: '{valor}' },
  { label: 'Fatura', token: '{fatura}' },
  { label: 'Vencimento', token: '{vencimento}' },
  { label: 'Link', token: '{link}' },
];

function normalizeVariableToken(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState<Template | null>(null);
  const [titulo, setTitulo] = useState('');
  const [conteudo, setConteudo] = useState('');
  const [imagemUrl, setImagemUrl] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [botoes, setBotoes] = useState<Botao[]>([]);
  const [novoBotaoTexto, setNovoBotaoTexto] = useState('');
  const [novoBotaoUrl, setNovoBotaoUrl] = useState('');
  const [novoBotaoRetorno, setNovoBotaoRetorno] = useState('');
  const [novoBotaoTipo, setNovoBotaoTipo] = useState<'reply' | 'link'>('reply');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const conteudoRef = useRef<HTMLTextAreaElement | null>(null);
  const [novaVariavel, setNovaVariavel] = useState('');

  const [confirmDelete, setConfirmDelete] = useState<Template | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await templatesApi.list();
      setTemplates(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openNovo = () => {
    setEditando(null);
    setTitulo('');
    setConteudo('');
    setImagemUrl('');
    setUploadingImg(false);
    setBotoes([]);
    setNovoBotaoTexto('');
    setNovoBotaoUrl('');
    setNovoBotaoRetorno('');
    setNovoBotaoTipo('reply');
    setNovaVariavel('');
    setError('');
    setModalOpen(true);
  };

  const openEditar = (t: Template) => {
    setEditando(t);
    setTitulo(t.titulo);
    setConteudo(t.conteudo);
    setImagemUrl(t.imagemUrl || '');
    setBotoes(t.botoes ?? []);
    setNovoBotaoTexto('');
    setNovoBotaoUrl('');
    setNovoBotaoRetorno('');
    setNovoBotaoTipo('reply');
    setNovaVariavel('');
    setError('');
    setModalOpen(true);
  };

  const adicionarBotao = () => {
    const texto = novoBotaoTexto.trim();
    if (!texto || botoes.length >= 3) return;
    if (novoBotaoTipo === 'link' && !novoBotaoUrl.trim()) return;
    if (novoBotaoTipo === 'reply' && !novoBotaoRetorno.trim()) return;
    const novo: Botao = novoBotaoTipo === 'link'
      ? { texto, url: novoBotaoUrl.trim() }
      : { texto, mensagemRetorno: novoBotaoRetorno.trim() };
    setBotoes(prev => [...prev, novo]);
    setNovoBotaoTexto('');
    setNovoBotaoUrl('');
    setNovoBotaoRetorno('');
  };

  const removerBotao = (i: number) => {
    setBotoes(prev => prev.filter((_, idx) => idx !== i));
  };

  const inserirVariavel = (token: string) => {
    const textarea = conteudoRef.current;
    if (!textarea) {
      setConteudo(prev => `${prev}${prev ? ' ' : ''}${token}`);
      return;
    }

    const start = textarea.selectionStart ?? conteudo.length;
    const end = textarea.selectionEnd ?? conteudo.length;
    const novoConteudo = `${conteudo.slice(0, start)}${token}${conteudo.slice(end)}`;
    setConteudo(novoConteudo);

    requestAnimationFrame(() => {
      textarea.focus();
      const pos = start + token.length;
      textarea.setSelectionRange(pos, pos);
    });
  };

  const adicionarVariavelPersonalizada = () => {
    const normalized = normalizeVariableToken(novaVariavel);
    if (!normalized) return;
    inserirVariavel(`{${normalized}}`);
    setNovaVariavel('');
  };

  const handleSalvar = async () => {
    if (!titulo.trim()) { setError('Informe o título do template'); return; }
    if (!conteudo.trim()) { setError('Informe o conteúdo do template'); return; }
    setError('');
    setSaving(true);
    try {
      const imgUrl = imagemUrl.trim() || undefined;
      if (editando) {
        await templatesApi.update(editando.id, titulo, conteudo, botoes, imgUrl);
      } else {
        await templatesApi.create(titulo, conteudo, botoes, imgUrl);
      }
      setModalOpen(false);
      loadAll();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Erro ao salvar template');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await templatesApi.remove(confirmDelete.id);
      setConfirmDelete(null);
      loadAll();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Erro ao excluir template');
    }
  };

  const filtered = templates.filter(t =>
    t.titulo.toLowerCase().includes(search.toLowerCase()) ||
    t.conteudo.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="bg-white border border-gray-200 p-4 flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={openNovo}
          className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-btn transition-opacity hover:opacity-80 flex-shrink-0"
          style={{ background: '#22C55E', color: '#FFFFFF' }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Nova Mensagem
        </button>
        <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-3 py-2 w-64 bg-white">
          <svg className="w-4 h-4 text-gray-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Pesquisar mensagem..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none"
          />
        </div>
      </div>

      {/* Tabela */}
      {loading ? (
        <p className="text-sm text-gray-400">Carregando...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 px-6 py-16 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(37, 99, 235, 0.15)' }}>
            <svg className="w-8 h-8" fill="none" stroke="#3B82F6" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V19a2 2 0 01-2 2zM13 3v4a1 1 0 001 1h4" />
            </svg>
          </div>
          <p className="text-base font-semibold text-gray-900">
            {templates.length === 0 ? 'Nenhum modelo cadastrado' : 'Nenhum modelo encontrado'}
          </p>
          <p className="text-sm text-gray-500 mt-1 max-w-sm">
            {templates.length === 0
              ? 'Crie modelos reutilizáveis para acelerar o envio das suas mensagens.'
              : 'Tente outro termo na busca.'}
          </p>
          {templates.length === 0 && (
            <button
              type="button"
              onClick={openNovo}
              className="mt-5 flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-btn transition-opacity hover:opacity-80"
              style={{ background: '#22C55E', color: '#FFFFFF' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Nova Mensagem
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto bg-white">
        <div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Título</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Botões</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Data</th>
              <th className="text-center px-4 py-2.5 text-xs font-semibold text-gray-400 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {filtered.map(t => (
              <tr key={t.id} className="hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0">
                <td className="px-4 py-3 text-sm text-gray-500 font-medium">{t.titulo}</td>
                <td className="px-4 py-3 text-center">
                  {(t.botoes ?? []).length > 0 ? (
                    <div className="flex flex-col gap-1.5 items-stretch">
                      {t.botoes.map((b, i) => (
                        <span
                          key={i}
                          className={`text-[10px] px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${ b.url ? 'border-[rgba(168,85,247,0.3)] bg-[rgba(168,85,247,0.15)] text-[#C4B5FD]' : 'border-blue-200 ' } bg-blue-50 text-blue-700`}
                        >
                          {b.url ? '🔗 ' : ''}{b.texto}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[11px] text-gray-300">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-gray-500 text-center whitespace-nowrap">{fmtDate(t.createdAt)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button type="button" title="Editar" onClick={() => openEditar(t)}
                      className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-blue-50 transition-all text-blue-400 hover:text-blue-700">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button type="button" title="Excluir" onClick={() => setConfirmDelete(t)}
                      className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-50 transition-all text-red-500 hover:text-red-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        </div>
      )}

      {/* Modal Criar/Editar */}
      {modalOpen && createPortal(
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden">
            <div className="relative px-7 py-5 border-b border-gray-100 flex-shrink-0 text-center bg-gray-50 z-10">
              <h3 className="text-base font-bold text-gray-900">{editando ? 'Editar Mensagem' : 'Nova Mensagem'}</h3>
              <button type="button" onClick={() => setModalOpen(false)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-7 py-5 flex flex-col gap-4 flex-1 overflow-y-auto">
              {/* Título */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Título</label>
                <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
                  placeholder="Ex: Promoção de verão"
                  className="w-full border rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-gray-500 bg-white border border-gray-200" />
              </div>

              {/* Conteúdo */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Conteúdo</label>
                <textarea ref={conteudoRef} value={conteudo} onChange={e => setConteudo(e.target.value)}
                  placeholder="Digite o conteúdo da mensagem..." rows={5}
                  className="w-full border rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-gray-500 resize-none bg-white border border-gray-200" />
                <p className="text-[11px] text-gray-400 mt-1 text-right">{conteudo.length} caracteres</p>
              </div>

              <div className="border border-[rgba(245,158,11,0.3)] bg-yellow-50 rounded-md p-4">
                <h4 className="text-sm font-semibold text-amber-900">Montar proposta</h4>
                <p className="text-xs text-yellow-700 mt-1 mb-3">Use variáveis para montar propostas personalizadas dentro da mensagem.</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {PROPOSAL_VARIABLES.map(v => (
                    <button
                      key={v.token}
                      type="button"
                      onClick={() => inserirVariavel(v.token)}
                      className="px-3 py-1.5 rounded-full border border-[rgba(245,158,11,0.3)] text-xs font-semibold text-yellow-700 hover:bg-[rgba(245,158,11,0.2)] transition-all bg-white border border-gray-200"
                    >
                      {v.label}: {v.token}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={novaVariavel}
                    onChange={e => setNovaVariavel(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        adicionarVariavelPersonalizada();
                      }
                    }}
                    placeholder="Criar variável personalizada. Ex: nome_cliente"
                    className="flex-1 border border-[rgba(245,158,11,0.3)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-amber-400 bg-white border border-gray-200"
                  />
                  <button
                    type="button"
                    onClick={adicionarVariavelPersonalizada}
                    disabled={!normalizeVariableToken(novaVariavel)}
                    className="px-3 py-2 rounded-md bg-amber-500 text-white text-sm font-semibold disabled:opacity-40 hover:bg-amber-600 transition-all"
                  >
                    Inserir
                  </button>
                </div>
                <div className="rounded-md border border-[rgba(245,158,11,0.3)] px-3 py-2 bg-white border border-gray-200">
                  <p className="text-[11px] font-semibold text-yellow-700 uppercase tracking-wide mb-1">Exemplo</p>
                  <p className="text-xs text-gray-600">Senhor Bruno, tenho uma proposta para o senhor. Sua fatura de {'{fatura}'} sai agora por {'{valor}'}.</p>
                </div>
              </div>

              {/* Upload de Imagem */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
                  Imagem <span className="text-gray-300 font-normal normal-case">(opcional)</span>
                </label>
                {imagemUrl ? (
                  <div className="relative inline-block">
                    <img
                      src={imagemUrl.startsWith('/uploads') ? `${API_URL}${imagemUrl}` : imagemUrl}
                      alt="preview"
                      className="rounded-md max-h-32 object-cover border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => setImagemUrl('')}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                    >×</button>
                  </div>
                ) : (
                  <label className={`flex items-center gap-2 px-4 py-2.5 border-2 border-dashed rounded-md cursor-pointer transition-all text-sm ${uploadingImg ? 'border-gray-300 text-gray-400' : 'border-gray-300 hover:border-gray-400 text-gray-500 hover:text-gray-700'}`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {uploadingImg ? 'Enviando...' : 'Clique para escolher imagem'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingImg}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        setUploadingImg(true);
                        try {
                          const { data } = await templatesApi.uploadImage(file);
                          setImagemUrl(data.url);
                        } catch {
                          setError('Erro ao enviar imagem');
                        }
                        setUploadingImg(false);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}
              </div>

              {/* Botões */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Botões</label>
                  <span className="text-[11px] text-gray-400">{botoes.length}/3</span>
                </div>

                {botoes.length > 0 && (
                  <div className="flex flex-col gap-2 mb-3">
                    {botoes.map((b, i) => (
                      <div key={i} className={`flex items-start justify-between gap-3 rounded-md px-3 py-2 border ${b.url ? 'bg-[rgba(168,85,247,0.15)] border-[rgba(168,85,247,0.3)]' : 'bg-blue-50 border-blue-200'}`} title={b.mensagemRetorno ? `Retorno: ${b.mensagemRetorno}` : ''}>
                        <div className="min-w-0">
                          <p className={`text-xs font-semibold ${b.url ? 'text-[#C4B5FD]' : 'text-blue-700'}`}>
                            Nome do botão: {b.texto}
                          </p>
                          <p className={`text-[11px] mt-1 break-words ${b.url ? 'text-purple-500' : 'text-blue-500'}`}>
                            {b.url ? `Link: ${b.url}` : `Mensagem de retorno: ${b.mensagemRetorno}`}
                          </p>
                        </div>
                        <span className={`hidden text-xs font-medium ${b.url ? 'text-[#C4B5FD]' : 'text-blue-700'}`}>
                          {b.url ? '🔗 ' : ''}{b.texto}
                        </span>
                        {b.mensagemRetorno && <span className="text-[10px] text-blue-400" title={b.mensagemRetorno}>↩</span>}
                        {b.url && <span className="text-[10px] text-purple-400 truncate max-w-[100px]">{b.url}</span>}
                        <button type="button" onClick={() => removerBotao(i)}
                          className={`ml-1 leading-none ${b.url ? 'text-purple-400 hover:text-[#C4B5FD]' : 'text-blue-400 hover:text-blue-700'}`}>×</button>
                      </div>
                    ))}
                  </div>
                )}

                {botoes.length < 3 && (
                  <div className="flex flex-col gap-2">
                    {/* Tipo de botão */}
                    <div className="flex gap-2">
                      <button type="button"
                        onClick={() => setNovoBotaoTipo('reply')}
                        className={`flex-1 py-1.5 text-xs rounded-md border font-medium transition-all ${novoBotaoTipo === 'reply' ? 'bg-blue-600 text-white border-blue-600' : ' text-gray-500 hover:border-gray-400'}`}>
                        Resposta rápida
                      </button>
                      <button type="button"
                        onClick={() => setNovoBotaoTipo('link')}
                        className={`flex-1 py-1.5 text-xs rounded-md border font-medium transition-all ${novoBotaoTipo === 'link' ? 'bg-purple-600 text-white border-purple-600' : ' text-gray-500 hover:border-gray-400'}`}>
                        🔗 Link
                      </button>
                    </div>

                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">Nome do botão</label>
                      <div className="flex gap-2">
                      <input type="text" value={novoBotaoTexto} onChange={e => setNovoBotaoTexto(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarBotao(); } }}
                        placeholder={novoBotaoTipo === 'link' ? 'Ex: Saiba mais' : 'Ex: Quero saber mais'}
                        maxLength={25}
                        className="flex-1 border rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-400 bg-white border border-gray-200" />
                      <button type="button" onClick={adicionarBotao}
                        disabled={!novoBotaoTexto.trim() || (novoBotaoTipo === 'link' && !novoBotaoUrl.trim()) || (novoBotaoTipo === 'reply' && !novoBotaoRetorno.trim())}
                        className={`px-3 py-2 text-white rounded-md text-sm font-medium transition-all disabled:opacity-40 ${novoBotaoTipo === 'link' ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
                        + Adicionar
                      </button>
                      </div>
                    </div>

                    {novoBotaoTipo === 'link' && (
                      <input type="url" value={novoBotaoUrl} onChange={e => setNovoBotaoUrl(e.target.value)}
                        placeholder="https://wa.me/5511999999999"
                        className="w-full border border-[rgba(168,85,247,0.3)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-purple-400 bg-white border border-gray-200" />
                    )}
                    {novoBotaoTipo === 'reply' && (
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Mensagem de retorno</label>
                        <textarea
                          value={novoBotaoRetorno}
                          onChange={e => setNovoBotaoRetorno(e.target.value)}
                          placeholder="Ex: Obrigado pelo seu retorno! Um consultor entrará em contato em breve."
                          rows={2}
                          className="w-full border border-[rgba(59,130,246,0.2)] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-blue-300 resize-none placeholder-gray-400 bg-white border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-gray-400 mt-1">
                  Máx. 3 botões · <span className="text-blue-500">Resposta rápida</span>: envia texto ao clicar · <span className="text-purple-500">Link</span>: abre URL
                </p>
              </div>

              {error && <p className="text-xs text-red-500">{error}</p>}
            </div>

            <div className="flex gap-3 px-7 pb-6 flex-shrink-0 border-t border-gray-100 bg-gray-50 pt-4">
              <button type="button" onClick={() => setModalOpen(false)}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-all">
                Cancelar
              </button>
              <button type="button" onClick={handleSalvar} disabled={saving}
                className="flex-1 py-2.5 bg-gray-900 hover:bg-gray-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-50">
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Confirmar Delete */}
      {confirmDelete && createPortal(
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="border rounded-lg shadow-2xl w-full max-w-sm bg-white border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-100 text-center">
              <h3 className="text-sm font-bold text-gray-900">Excluir mensagem</h3>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 text-center">
                Deseja excluir a mensagem <span className="font-semibold text-gray-900">"{confirmDelete.titulo}"</span>?
              </p>
            </div>
            <div className="flex gap-3 px-6 pb-5">
              <button type="button" onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-all">
                Cancelar
              </button>
              <button type="button" onClick={handleDelete}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold transition-all">
                Excluir
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
